import { useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { Article } from '@troc/shared'
import { api, ErreurApi } from '../api'
import { useToast } from '../etat/Toast'
import type { ResultatScan } from './ScannerCamera'

/**
 * Lire une étiquette du rayon en la photographiant.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Le lecteur code-barres ne sert à rien sur ces étiquettes
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Les 80 000 pièces du rayon portent une étiquette imprimée en texte seul — le
 * numéro, la désignation, le prix. Aucun code-barres, et personne ne va
 * réétiqueter le magasin. On photographie donc l'étiquette et le serveur y
 * cherche le numéro par reconnaissance de texte.
 *
 * Le numéro lu est confronté à la base avant tout : une lecture bruitée ne
 * correspond à aucun article, et rien n'entre au ticket ni dans une reprise.
 *
 * L'image est réduite avant l'envoi : une photo d'iPad pèse quatre méga-octets,
 * une largeur de 1600 pixels suffit largement à lire des chiffres imprimés.
 */
export function PhotoEtiquette({
  onArticle,
  libelle = 'Photographier l’étiquette',
  className = 'btn btn-secondaire btn-bloc btn-centre',
  style,
}: {
  /**
   * Range l'article lu là où il doit aller — le ticket, la liste de reprise —
   * et dit si c'était possible. Un refus (`ok: false`) est montré tel quel.
   */
  onArticle: (article: Article, lu: string) => ResultatScan
  libelle?: string
  className?: string
  style?: CSSProperties
}) {
  const { signaler, alerter } = useToast()
  /** Lecture en cours — la reconnaissance prend une seconde. */
  const [lecture, setLecture] = useState(false)
  const champ = useRef<HTMLInputElement>(null)

  const lire = async (fichier: File) => {
    setLecture(true)
    try {
      const image = await createImageBitmap(fichier, { imageOrientation: 'from-image' })
      const echelle = Math.min(1, 1600 / Math.max(image.width, image.height))
      const toile = document.createElement('canvas')
      toile.width = Math.round(image.width * echelle)
      toile.height = Math.round(image.height * echelle)
      toile.getContext('2d')!.drawImage(image, 0, 0, toile.width, toile.height)
      const reponse = await api<{ article: Article; lu: string }>('/lecture/etiquette', {
        methode: 'POST',
        corps: { image: toile.toDataURL('image/jpeg', 0.75) },
      })
      const suite = onArticle(reponse.article, reponse.lu)
      if (suite.ok) signaler(`Étiquette lue : ${reponse.lu}`)
      else alerter(suite.detail ?? suite.libelle)
    } catch (cause) {
      alerter(
        cause instanceof ErreurApi
          ? cause.message
          : 'Lecture impossible — rapprochez-vous de l’étiquette.',
      )
    } finally {
      setLecture(false)
      if (champ.current) champ.current.value = ''
    }
  }

  return (
    <>
      <input
        ref={champ}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void lire(f)
        }}
      />
      <button
        type="button"
        className={className}
        style={style}
        disabled={lecture}
        onClick={() => champ.current?.click()}
      >
        {lecture ? 'Lecture en cours…' : libelle}
      </button>
    </>
  )
}
